import React from 'react';

import { FormattedMessage } from 'react-intl';
import { all, call, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { listSavedFilingPeriodFilesRequest } from '@actions/savedFilingPeriod';
import * as actions from '@actions/standardTemplate';
import { getEcslEntryTotals } from '@api/aggregates';
import {
  createStandardTemplate,
  deleteStandardTemplate,
  downloadStandardTemplate,
  getFilingDataFromPreviousReturn,
  getStandardTemplateAggregate,
  getStandardTemplateJobs,
  getStandardTemplates,
  processStandardTemplates,
  uploadStandardTemplate,
} from '@api/standardTemplate';
import types from '@constants/action-types';
import { CATEGORY_CHOICES } from '@constants/vatReturns';
import { handleRequest } from '@sagas/request';
import { processStandardTemplatesSaga } from '@sagas/standardTemplate/processStandardTemplatesSaga';
import { toastError } from '@store/notifications';
import {
  troubleshootingDeleteRequest,
  troubleshootingEditRequest,
  troubleshootingIgnoreRequest,
} from '@store/troubleshooting';

import { handleSaveManualAdjustments } from './handleSaveManualAdjustments';
import { processingAggregatesRequestSaga } from './processingAggregatesRequestSaga';
import { watchPollProcessingAggregatesSaga } from './watchPollProcessingAggregatesSage';

export const handleGetStandardTemplatesRequest = function* ({ payload }) {
  try {
    const response = yield call(getStandardTemplates, payload);
    if (response.isAxiosError)
      return yield put(actions.getStandardTemplatesFailure(response.message));
    yield put(actions.getStandardTemplatesSuccess(response));
  } catch (e) {
    yield put(actions.getStandardTemplatesFailure(e));
  }
};

export const handleUploadStandardTemplateRequest = function* ({ payload }) {
  try {
    const response = yield call(uploadStandardTemplate, payload);
    if (response.isAxiosError)
      return yield put(actions.uploadStandardTemplateFailure(response.message));
    yield put(actions.uploadStandardTemplateSuccess(response));
  } catch (e) {
    yield put(actions.uploadStandardTemplateFailure(e));
  }
};

export const handleCreateStandardTemplateRequest = function* ({ payload }) {
  try {
    const templatesResponse = yield call(getStandardTemplates, payload);
    if (templatesResponse.results.length < 1) {
      const response = yield call(createStandardTemplate, payload);
      if (response.isAxiosError)
        return yield put(actions.createStandardTemplateFailure(response.message));
      yield put(actions.createStandardTemplateSuccess(response));
    }
  } catch (e) {
    yield put(actions.createStandardTemplateFailure(e));
  }
};

export const handleDownloadStandardTemplateRequest = function* ({ payload }) {
  try {
    const response = yield call(downloadStandardTemplate, payload);
    if (response.isAxiosError)
      return yield put(actions.downloadStandardTemplateFailure(response.message));
    yield put(actions.downloadStandardTemplateSuccess(response));
  } catch (e) {
    yield put(actions.downloadStandardTemplateFailure(e));
  }
};

export const handleDeleteStandardTemplateRequest = function* ({ payload }) {
  try {
    const response = yield call(deleteStandardTemplate, payload);
    if (response.isAxiosError)
      return yield put(actions.deleteStandardTemplateFailure(response.message));
    yield put(actions.deleteStandardTemplateSuccess(response));
  } catch (e) {
    yield put(actions.deleteStandardTemplateFailure(e));
  }
};

export const handleProcessStandardTemplatesRequest = function* ({ payload }) {
  try {
    const response = yield call(processStandardTemplates, payload);
    if (response.isAxiosError)
      return yield put(actions.processStandardTemplatesFailure(response.message));
    yield put(actions.processStandardTemplatesSuccess(response));
  } catch (e) {
    yield put(actions.processStandardTemplatesFailure(e));
  }
};

export const handleGetStandardTemplateJobsRequest = function* ({ payload }) {
  try {
    const response = yield call(getStandardTemplateJobs, payload);
    if (response.isAxiosError)
      return yield put(actions.getStandardTemplateJobsFailure(response.message));
    yield put(actions.getStandardTemplateJobsSuccess(response));
  } catch (e) {
    yield put(actions.getStandardTemplateJobsFailure(e));
  }
};

export const handleGetStandardTemplateAggregatesRequest = function* ({ payload }) {
  try {
    const response = yield call(getStandardTemplateAggregate, payload);
    if (response.isAxiosError)
      return yield put(actions.getStandardTemplateAggregatesFailure(response.message));
    yield put(actions.getStandardTemplateAggregatesSuccess(response));
  } catch (e) {
    yield put(actions.getStandardTemplateAggregatesFailure(e));
  }
};

export const handleGetStandardTemplateEcslRequest = function* ({ payload }) {
  try {
    const response = yield call(getEcslEntryTotals, payload.standardTemplateId);
    if (response.isAxiosError)
      return yield put(actions.getStandardTemplateEcslFailure(response.message));
    yield put(actions.getStandardTemplateEcslSuccess({ ...response, type: payload.type }));
  } catch (e) {
    yield put(actions.getStandardTemplateEcslFailure(e));
  }
};

export const handleGetPreviousFilingData = function* ({ payload }) {
  const { filingPeriodId, templateId } = payload;
  try {
    yield call(getFilingDataFromPreviousReturn, templateId);
    yield put(
      listSavedFilingPeriodFilesRequest({
        id: filingPeriodId,
        categories: [CATEGORY_CHOICES.CALCULATIONS],
      })
    );
    yield put(actions.getPreviousFilingDataSuccess());
  } catch (error) {
    const { nonFieldErrors } = error.data || {};
    yield put([
      toastError({
        message: nonFieldErrors || (
          <FormattedMessage
            defaultMessage="An unexpected error occurred, please try again."
            id="global.notification.api-error-default"
          />
        ),
      }),
      actions.getPreviousFilingDataFailure(),
    ]);
  }
};

export default all([
  fork(watchPollProcessingAggregatesSaga),
  takeEvery(types.UPLOAD_STANDARD_TEMPLATE_REQUEST, handleUploadStandardTemplateRequest),
  takeEvery(types.CREATE_STANDARD_TEMPLATE_REQUEST, handleCreateStandardTemplateRequest),
  takeEvery(types.DOWNLOAD_STANDARD_TEMPLATE_REQUEST, handleDownloadStandardTemplateRequest),
  takeEvery(types.DELETE_STANDARD_TEMPLATE_REQUEST, handleDeleteStandardTemplateRequest),
  takeEvery(types.GET_STANDARD_TEMPLATES_REQUEST, handleGetStandardTemplatesRequest),
  takeEvery(types.PROCESS_STANDARD_TEMPLATES_REQUEST, processStandardTemplatesSaga),
  takeEvery(types.GET_STANDARD_TEMPLATE_JOBS_REQUEST, handleGetStandardTemplateJobsRequest),
  takeEvery(
    types.GET_STANDARD_TEMPLATE_AGGREGATES_REQUEST,
    handleGetStandardTemplateAggregatesRequest
  ),
  takeEvery(types.GET_STANDARD_TEMPLATE_ECSL_REQUEST, handleGetStandardTemplateEcslRequest),
  takeEvery(types.SAVE_MANUAL_ADJUSTMENTS, handleSaveManualAdjustments),
  takeEvery(types.GET_PREVIOUS_FILING_DATA_REQUEST, handleGetPreviousFilingData),
  takeEvery(troubleshootingIgnoreRequest.type, handleRequest),
  takeEvery(troubleshootingEditRequest.type, handleRequest),
  takeEvery(troubleshootingDeleteRequest.type, handleRequest),
  takeLatest(types.FETCH_PROCESSING_AGGREGATES_REQUEST, processingAggregatesRequestSaga),
]);
